import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getProductById, getAllProducts } from "../../sanity/queries";
import type { SanityProduct } from "../../sanity/types";
import ProductGallery from "./components/ProductGallery";
import ProductInfo from "./components/ProductInfo";
import ProductDetails from "./components/ProductDetails";
import Footer from "../home/components/Footer";

export default function ProductPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<SanityProduct | null | undefined>(undefined);
  const [related, setRelated] = useState<SanityProduct[]>([]);
  const [redirectSeconds, setRedirectSeconds] = useState(20);

  useEffect(() => {
    if (!id) return;
    setProduct(undefined);
    getProductById(id).then((p) => setProduct(p ?? null));
  }, [id]);

  useEffect(() => {
    if (!product) return;
    getAllProducts().then((all) =>
      setRelated(all.filter((p) => p.category === product.category && p._id !== product._id).slice(0, 3))
    );
  }, [product]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (product !== null) return;

    setRedirectSeconds(20);
    const interval = setInterval(() => {
      setRedirectSeconds((prev) => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    const timeout = setTimeout(() => {
      navigate("/", { replace: true });
    }, 20000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [product, navigate]);

  if (product === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#144E5D] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (product === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-[#F0F7F8] gap-5 px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-[#144E5D]/10 text-[#144E5D] flex items-center justify-center animate-pulse">
          <i className="ri-error-warning-line text-5xl"></i>
        </div>
        <h2 className="text-3xl font-bold text-gray-800">Product not found</h2>
        <p className="text-sm text-gray-500 max-w-md">
          This product may have been removed or the link might be incorrect. You
          will be redirected to the home page shortly.
        </p>
        <p className="text-sm font-semibold text-[#144E5D]">
          Redirecting in {redirectSeconds}s...
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#144E5D] text-white hover:bg-[#B8D8DE] hover:text-[#144E5D] transition-colors cursor-pointer whitespace-nowrap"
        >
          Go Home Now <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFB]">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/000f6613bbd5bf7f02c140851804a982/edb968f21dc844722d3645b2f59fd129.png"
              alt="Curewell Logo"
              className="h-9 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#144E5D] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Products
          </Link>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <nav className="flex items-center gap-2 text-xs text-gray-400">
          <Link
            to="/"
            className="hover:text-[#144E5D] transition-colors cursor-pointer whitespace-nowrap"
          >
            Home
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link
            to="/"
            className="hover:text-[#144E5D] transition-colors cursor-pointer whitespace-nowrap"
          >
            Products
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-600 font-medium truncate max-w-xs">
            {product.name}
          </span>
        </nav>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 pb-20">
        {/* Product Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <ProductGallery images={product.images} name={product.name} />
          <ProductInfo product={product} />
        </div>

        {/* Product Details */}
        <div className="grid grid-cols-1 gap-6 mb-12">
          <ProductDetails product={product} />
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Related Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((rel) => (
                <Link
                  key={rel._id}
                  to={`/product/${rel._id}`}
                  className="bg-white rounded-xl border border-gray-200 hover:border-[#144E5D] hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                >
                  <div className="h-1.5 bg-[#144E5D]"></div>
                  <div className="w-full h-40 bg-[#F0F7F8] flex items-center justify-center p-4">
                    <img
                      src={rel.imageUrl ?? ""}
                      alt={rel.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <span className="text-xs font-semibold text-[#144E5D] bg-[#144E5D]/10 px-2 py-0.5 rounded-full w-fit mb-2 whitespace-nowrap">
                      {rel.category}
                    </span>
                    <h3 className="text-sm font-bold text-gray-800 mb-1 leading-tight">
                      {rel.name}
                    </h3>
                    <p className="text-xs text-gray-400 mb-3">{rel.subtitle}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <span className="text-lg font-bold text-[#144E5D]">
                        {rel.price}
                      </span>
                      <span className="text-xs text-[#144E5D] font-medium inline-flex items-center gap-1">
                        View <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
